// Benepack OMS · AntD ThemeConfig
// 来源 SSOT: docs/ui/theme.md §4.2 真值 (Jason 2026-05-16 LOGO patch 拍板)
//   - Benepack Blue #4A6FAF (primary)
//   - Benepack Green #7CB342 (success / brand accent)
//   - 侧边栏深色 #001529 (跟主色同色系)
import type { ThemeConfig } from 'antd';

export const benepackTheme: ThemeConfig = {
  token: {
    colorPrimary: '#4A6FAF',
    colorSuccess: '#7CB342',
    colorWarning: '#FAAD14',
    colorError: '#F5222D',
    colorInfo: '#4A6FAF',
    borderRadius: 6,
    fontSize: 14,
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", "Segoe UI", Roboto, sans-serif',
  },
  components: {
    Layout: {
      siderBg: '#001529',
      headerBg: '#FFFFFF',
      bodyBg: '#F5F7FA',
      headerPadding: '0 24px',
      headerHeight: 56,
    },
    Menu: {
      darkItemSelectedBg: '#4A6FAF',
      darkItemBg: '#001529',
      darkSubMenuItemBg: '#000C17',
    },
    Card: {
      paddingLG: 24,
    },
    Table: {
      cellPaddingBlock: 10,
      cellPaddingInline: 12,
    },
  },
};

// brand 调色板 (跟 theme.md §1 同步 · 业务方后期可调)
export const brand = {
  blue: '#4A6FAF',
  blueDark: '#001529',
  green: '#7CB342',
  bgPage: '#F5F7FA',
  bgCard: '#FFFFFF',
  borderLight: '#E5E7EB',
  textPrimary: 'rgba(0, 0, 0, 0.88)',
  textSecondary: 'rgba(0, 0, 0, 0.65)',
  textTertiary: 'rgba(0, 0, 0, 0.45)',
} as const;

// =====================================================
// S6 设计令牌 (图表序列 / 灰阶 / 语义状态色 · 组件引用 token 杜绝散落内联 hex)
//   状态 Tag 走 antd 语义 color (default/processing/warning/success/error · 见 S2 table-columns)；
//   本层补"非 Tag 场景"(图表 / 卡主题色 / 逾期标红 / 涨跌色 / 灰阶) 的命名令牌。
// =====================================================

// 图表 / 卡 品牌色板 (以 #4A6FAF 蓝 + #7CB342 绿 为主 · 替代原 Mantine 杂色板)
export const chartColors = {
  blue: '#4A6FAF',
  green: '#7CB342',
  amber: '#E8943A',
  teal: '#3FA7A0',
  purple: '#8E6FD0',
  lightBlue: '#7AA0D8',
  coral: '#D9655B',
  gold: '#C2A23A',
  cyan: '#2F8FA8',
  rust: '#C2683A',
} as const;

// 灰阶 (合理值 · 不作 AA 门槛[决策 4] · 替代散落 #8c8c8c / #bfbfbf / #868e96)
export const gray = {
  muted: '#8C8C8C', // 次要文本 / 占位
  disabled: '#BFBFBF', // 失效 / 降级值
  borderSplit: '#F0F0F0', // 分隔线
} as const;

// 语义状态色 (与 antd Tag token 同源 · 供非 Tag 场景[逾期标红 / 降级]引用)
export const statusColors = {
  processing: '#4A6FAF', // = colorInfo / colorPrimary
  success: '#7CB342', // = colorSuccess
  warning: '#FAAD14', // = colorWarning
  error: '#F5222D', // = colorError
  errorStrong: '#CF1322', // 逾期 / 告警强调红 (表格红字 · 深一档)
  errorBg: '#FFF1F0', // 逾期行淡红底
} as const;

// 中式涨跌色 (涨红跌绿 · 财务惯例 · 与状态色语义区分)
export const trendColors = {
  up: '#CF1322',
  down: '#3F8600',
} as const;

// 实体主题色 (workbench 卡 / 报表指标卡 / 排名榜 同实体同色 · 单一真值源 · 品牌化)
export const entityColors = {
  quotation: chartColors.blue,
  salesOrder: chartColors.green,
  purchaseOrder: chartColors.amber,
  invoice: chartColors.purple,
  ar: chartColors.teal,
  ap: chartColors.coral,
  statement: chartColors.lightBlue,
  batch: chartColors.gold,
  audit: gray.muted,
} as const;
